import { useState } from 'react';

interface Step3Data {
  startDate: string;
  endDate: string;
  flexibility: string;
  duration: string;
}

interface Step3Props {
  data: Step3Data;
  onChange: (data: Partial<Step3Data>) => void;
  onNext: () => void;
  onBack: () => void;
}

const FLEXIBILITY_OPTIONS = [
  { value: 'fixed', label: 'Data fixada', icon: 'ri-calendar-check-line', desc: 'A data já está confirmada' },
  { value: 'flexible-1w', label: 'Flexível ±1 semana', icon: 'ri-calendar-2-line', desc: 'Poderia mudar até 1 semana' },
  { value: 'flexible-1m', label: 'Flexível ±1 mês', icon: 'ri-calendar-line', desc: 'Mês aproximado definido' },
  { value: 'tbd', label: 'Ainda sem data', icon: 'ri-question-line', desc: 'Apenas explorando opções' },
];

const DURATION_OPTIONS = [
  { value: 'meio-dia', label: 'Meio período', desc: 'Até 4 horas' },
  { value: '1-dia', label: '1 dia', desc: 'Das 8h às 18h' },
  { value: '2-dias', label: '2 dias', desc: 'Evento de 2 dias' },
  { value: '3-dias', label: '3 dias', desc: 'Evento de 3 dias' },
  { value: '4-5-dias', label: '4–5 dias', desc: 'Evento de longa duração' },
  { value: 'mais-5-dias', label: 'Mais de 5 dias', desc: 'Congressos longos' },
];

function formatDateBR(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function getDaysDiff(start: string, end: string): number {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  const diff = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
}

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0];
}

export default function Step3DateRange({ data, onChange, onNext, onBack }: Step3Props) {
  const [dateError, setDateError] = useState('');

  const today = getTodayStr();

  const handleStartDate = (val: string) => {
    setDateError('');
    onChange({ startDate: val });
    // If endDate is before new startDate, clear it
    if (data.endDate && val > data.endDate) {
      onChange({ startDate: val, endDate: '' });
    } else {
      onChange({ startDate: val });
    }
  };

  const handleEndDate = (val: string) => {
    setDateError('');
    if (data.startDate && val < data.startDate) {
      setDateError('A data de término deve ser igual ou posterior à data de início.');
      return;
    }
    onChange({ endDate: val });
  };

  const daysDiff = getDaysDiff(data.startDate, data.endDate);

  const canProceed = data.flexibility !== '' && (
    data.flexibility === 'tbd' || (data.startDate !== '' && data.endDate !== '')
  );

  const isSingleDay = data.startDate === data.endDate && data.startDate !== '';

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary mb-2">
          Quando será o evento?
        </h2>
        <p className="font-body text-gray-500 text-base">
          Selecione o período — início e término — e nos diga o nível de flexibilidade da data.
        </p>
      </div>

      {/* Flexibility */}
      <div className="mb-8">
        <label className="block font-body text-sm font-semibold text-secondary mb-4">
          Situação da data <span className="text-primary">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {FLEXIBILITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ flexibility: opt.value })}
              className={`p-4 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer group ${
                data.flexibility === opt.value
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-primary/40 hover:bg-gray-50'
              }`}
            >
              <div
                className={`w-9 h-9 flex items-center justify-center rounded-lg mb-3 transition-colors duration-200 ${
                  data.flexibility === opt.value ? 'bg-primary/15' : 'bg-gray-100 group-hover:bg-primary/10'
                }`}
              >
                <i
                  className={`${opt.icon} text-base transition-colors duration-200 ${
                    data.flexibility === opt.value ? 'text-primary' : 'text-gray-500 group-hover:text-primary'
                  }`}
                ></i>
              </div>
              <p
                className={`font-body font-semibold text-xs mb-1 ${
                  data.flexibility === opt.value ? 'text-primary' : 'text-secondary'
                }`}
              >
                {opt.label}
              </p>
              <p className="font-body text-xs text-gray-400 leading-snug hidden sm:block">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Date range — only show if not TBD */}
      {data.flexibility !== 'tbd' && (
        <div className="mb-8">
          <label className="block font-body text-sm font-semibold text-secondary mb-4">
            Período do evento <span className="text-primary">*</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Start */}
            <div>
              <label className="block font-body text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">
                Data de início
              </label>
              <div className="relative">
                <input
                  type="date"
                  min={today}
                  value={data.startDate}
                  onChange={(e) => handleStartDate(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-body text-gray-800 bg-white focus:border-primary focus:outline-none transition-colors cursor-pointer"
                />
                {data.startDate && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-check-line text-primary text-xs"></i>
                    </div>
                    <span className="text-xs text-primary font-semibold">{formatDateBR(data.startDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* End */}
            <div>
              <label className="block font-body text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">
                Data de término
              </label>
              <div className="relative">
                <input
                  type="date"
                  min={data.startDate || today}
                  value={data.endDate}
                  onChange={(e) => handleEndDate(e.target.value)}
                  disabled={!data.startDate}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-body text-gray-800 bg-white focus:border-primary focus:outline-none transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {data.endDate && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-check-line text-primary text-xs"></i>
                    </div>
                    <span className="text-xs text-primary font-semibold">{formatDateBR(data.endDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error */}
          {dateError && (
            <p className="mt-2 text-xs text-red-500 font-medium flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              {dateError}
            </p>
          )}

          {/* Duration summary */}
          {data.startDate && data.endDate && !dateError && (
            <div className="mt-4 flex items-center gap-3 p-4 bg-primary/5 border border-primary/20 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center bg-primary/15 rounded-full flex-shrink-0">
                <i className="ri-time-line text-primary text-lg"></i>
              </div>
              <div>
                {isSingleDay ? (
                  <p className="font-body font-semibold text-secondary text-sm">
                    Evento em <strong className="text-primary">{formatDateBR(data.startDate)}</strong> (1 dia)
                  </p>
                ) : (
                  <p className="font-body font-semibold text-secondary text-sm">
                    De <strong className="text-primary">{formatDateBR(data.startDate)}</strong> até{' '}
                    <strong className="text-primary">{formatDateBR(data.endDate)}</strong>
                    {daysDiff > 0 && (
                      <span className="ml-2 text-gray-500 font-normal">
                        ({daysDiff + 1} {daysDiff + 1 === 1 ? 'dia' : 'dias'})
                      </span>
                    )}
                  </p>
                )}
                <p className="font-body text-xs text-gray-400 mt-0.5">
                  O orçamento será calculado com base neste período
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {data.flexibility === 'tbd' && (
        <div className="mb-8 p-5 bg-gray-50 rounded-2xl border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-full flex-shrink-0">
              <i className="ri-lightbulb-line text-primary"></i>
            </div>
            <div>
              <p className="font-body font-semibold text-secondary text-sm mb-1">
                Sem problema! Podemos ajudar com o planejamento.
              </p>
              <p className="font-body text-xs text-gray-500 leading-relaxed">
                Mesmo sem data definida, nosso time pode apresentar simulações de custos e disponibilidade para que você tome a melhor decisão.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Duration (optional) */}
      {data.flexibility !== 'tbd' && data.startDate && data.endDate && (
        <div className="mb-8">
          <label className="block font-body text-sm font-semibold text-secondary mb-3">
            Duração por dia (opcional)
          </label>
          <div className="flex flex-wrap gap-2">
            {DURATION_OPTIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => onChange({ duration: d.value })}
                className={`px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  data.duration === d.value
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-primary/10 hover:text-primary'
                }`}
              >
                {d.label}
                <span className="ml-1 opacity-60 text-xs">({d.desc})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-body font-semibold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-left-line"></i>
          </div>
          Voltar
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          Continuar
          <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-arrow-right-line text-white"></i>
          </div>
        </button>
      </div>
    </div>
  );
}